import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  CircularProgress,
  Checkbox,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import debounce from "lodash/debounce";
import AppForm from "components/Shared/AppForm";
import { useAuth } from "hooks/useAuth";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  useLazyGetDDLShipmentForRefundLiteQuery,
  useLazyGetDDLShipmentPriceListQuery,
} from "store/services/PaymentsApi/shipmentRefundApi";
import AppSwitch from "components/Shared/AppSwitch";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";
import { toast } from "react-toastify";

const REFUND_STATUSES = ["Pending", "Approved", "Rejected"];
const REFUND_TYPES_WITHOUT_CHARGES_OR_COD = [
  "Compensation For Loss/ Damage",
  "Overpayment",
];

const getDateAuto = () => new Date().toISOString().slice(0, 10);

const INITIAL_STATE = {
  account: null,
  entity: null,
  shipment: null,
  shipmentPrice: [],
  shipmentPriceAmounts: {},
  vendor: null,
  customer: null,
  amount: "",
  refundType: null,

  isTransferToCOD: false,
  codAmount: "",
  originalChargeAmount: 0,
  adjustedChargeAmount: 0,
  adjustedPrice: null,
  adjustmentMessage: "",
  refundStatus: "Pending",
  refundDate: getDateAuto(),
  refundReason: "",
};

const ShipmentRefundForm = ({
  isBusy = false,
  shipmentRefundDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
  const [shipmentSearchInput, setShipmentSearchInput] = useState("");

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchShipments, { data: shipments = [], isLoading: loadingShipments }] =
    useLazyGetDDLShipmentForRefundLiteQuery();

  const [
    fetchShipmentPrices,
    { data: priceData, isLoading: loadingShipmentPrices },
  ] = useLazyGetDDLShipmentPriceListQuery();

  const {
    data: refundTypeList = [],
    isLoading: isLoadingRefundTypes,
    isFetching: isFetchingRefundTypes,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.REFUND_TYPES, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );



  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
    },
    mode: "onChange",
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchShipment = useWatch({ control, name: "shipment" });
  const _watchRefundType = useWatch({ control, name: "refundType" });

  const _watchIsTransferToCOD = useWatch({ control, name: "isTransferToCOD" });
  const _watchCodAmount = useWatch({ control, name: "codAmount" });
  const _watchOriginalCharge = useWatch({ control, name: "originalChargeAmount" });
  const watchedShipmentPrice = useWatch({ control, name: "shipmentPrice" });
  const watchedShipmentPriceAmounts = useWatch({ control, name: "shipmentPriceAmounts" });
  const _watchShipmentPrice = useMemo(
    () => (Array.isArray(watchedShipmentPrice) ? watchedShipmentPrice : []),
    [watchedShipmentPrice]
  );
  const shipmentPriceAmounts = useMemo(
    () => watchedShipmentPriceAmounts || {},
    [watchedShipmentPriceAmounts]
  );
  const isCodTransfer = Boolean(_watchIsTransferToCOD);
  const hideChargesAndCod = REFUND_TYPES_WITHOUT_CHARGES_OR_COD.includes(
    _watchRefundType?.label
  );

  // Shipment type flags
  const isVendorShipment = !!_watchShipment?.vendor;
  const isCustomerShipment = !!_watchShipment?.customerId && !_watchShipment?.vendor;

  const shipmentOptions = useMemo(() => {
    const baseOptions = Array.isArray(shipments) ? shipments : [];
    const selectedShipment = shipmentRefundDetail?.shipment;
    if (!selectedShipment?._id) return baseOptions;
    const exists = baseOptions.some((item) => item?._id === selectedShipment._id);
    return exists ? baseOptions : [selectedShipment, ...baseOptions];
  }, [shipments, shipmentRefundDetail?.shipment]);

  // Vendor options derived from selected shipment
  const vendorOptions = useMemo(() => {
    if (!_watchShipment?.vendor) return [];
    return [_watchShipment.vendor];
  }, [_watchShipment]);

  // Customer options derived from selected shipment 
  const customerOptions = useMemo(() => {
    if (!_watchShipment?.customerId) return [];
    return [_watchShipment.customerId];
  }, [_watchShipment]);

  const _mappedRefundTypes = useMemo(
    () =>
      Array.isArray(refundTypeList)
        ? refundTypeList.map((item) => ({
          label: item?.name || "",
          _id: item?._id || "",
        }))
        : [],
    [refundTypeList]
  );

  // Load available lines without selecting them. The user must explicitly
  // choose each line and, for COD transfers, its full or partial amount.
  useEffect(() => {
    if (priceData?.prices?.length) {
      const chargePrices = priceData.prices.filter((price) => price.chargeType === "Charge");
      setValue("shipmentPrice", []);
      setValue("shipmentPriceAmounts", {});
      const chargeTotal = chargePrices.reduce((sum, price) => sum + Number(price.charge || 0), 0);
      setValue("originalChargeAmount", chargeTotal);
      if (chargePrices[0]?._id) {
        setValue("adjustedPrice", chargePrices[0]._id);
      }
      if (!isCodTransfer) setValue("amount", priceData.totalCharge ?? "");
    }
  }, [
    priceData,
    setValue,
    isCodTransfer,
  ]);

  const selectedRefundPrices = useMemo(
    () => (priceData?.prices || []).filter((price) => _watchShipmentPrice.includes(price._id)),
    [priceData?.prices, _watchShipmentPrice]
  );

  const selectedCodAmount = useMemo(
    () =>
      selectedRefundPrices.reduce(
        (sum, price) => sum + Number(shipmentPriceAmounts[price._id] ?? price.charge ?? 0),
        0
      ),
    [selectedRefundPrices, shipmentPriceAmounts]
  );

  const selectedRefundAmount = useMemo(
    () =>
      selectedRefundPrices.reduce(
        (sum, price) => sum + Number(shipmentPriceAmounts[price._id] ?? price.charge ?? 0),
        0
      ),
    [selectedRefundPrices, shipmentPriceAmounts]
  );

  useEffect(() => {
    if (isCodTransfer) {
      setValue("codAmount", selectedCodAmount);
      setValue("amount", selectedCodAmount);
      setValue(
        "adjustedChargeAmount",
        Math.max(Number(_watchOriginalCharge || priceData?.totalCharge || 0) - selectedCodAmount, 0)
      );
    }
  }, [isCodTransfer, selectedCodAmount, _watchOriginalCharge, priceData?.totalCharge, setValue]);

  useEffect(() => {
    if (
      !isCodTransfer &&
      priceData?.prices &&
      (!hideChargesAndCod || _watchShipmentPrice.length > 0)
    ) {
      setValue("amount", selectedRefundAmount);
    }
  }, [
    isCodTransfer,
    hideChargesAndCod,
    _watchShipmentPrice.length,
    priceData?.prices,
    selectedRefundAmount,
    setValue,
  ]);

  // Sync COD transfer values
  useEffect(() => {
    if (isCodTransfer) {
      setValue("refundType", null);
      if (_watchCodAmount) {
        setValue("amount", _watchCodAmount);
      }
      const original = Number(_watchOriginalCharge) || Number(priceData?.totalCharge) || 0;
      const cod = Number(_watchCodAmount) || 0;
      setValue("adjustedChargeAmount", Math.max(original - cod, 0));
    } else if (!hideChargesAndCod) {
      setValue("amount", priceData?.totalCharge ?? "");
    }
  }, [isCodTransfer, hideChargesAndCod, _watchCodAmount, _watchOriginalCharge, priceData?.totalCharge, setValue]);

  // Submit
  const handleOnSubmit = (data) => {
    const isRefundWithoutChargesOrCod = REFUND_TYPES_WITHOUT_CHARGES_OR_COD.includes(
      data?.refundType?.label
    );
    if (!isRefundWithoutChargesOrCod && !data?.shipmentPrice?.length) {
      toast.error("Please select at least one collected price line to refund.");
      return;
    }
    const isCod = Boolean(data.isTransferToCOD);
    const invalidAdjustment = !isRefundWithoutChargesOrCod && (data.shipmentPrice || []).some((priceId) => {
        const price = priceData?.prices?.find((item) => item._id === priceId);
        const adjustment = Number(data.shipmentPriceAmounts?.[priceId] || 0);
        return !price || adjustment <= 0 || adjustment > Number(price.charge || 0);
      });
    const submittedAmount = isRefundWithoutChargesOrCod
      ? Number(data.amount)
      : selectedRefundAmount;
    if (invalidAdjustment || !Number.isFinite(submittedAmount) || submittedAmount <= 0) {
      toast.error(
        isRefundWithoutChargesOrCod
          ? "Enter a valid refund amount."
          : "Enter a valid partial or full amount for every selected price line."
      );
      return;
    }
    const originalCharge = Number(data.originalChargeAmount || priceData?.totalCharge || 0);
    const codAmt = isCod ? Number(selectedCodAmount || 0) : 0;
    const remainingCharge = isCod ? Math.max(originalCharge - codAmt, 0) : 0;

    onSubmit({
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipment: data?.shipment?._id,
      shipmentPrice: data?.shipmentPrice ?? [],
      shipmentPriceAdjustments: (data?.shipmentPrice ?? []).map((priceId) => ({
          shipmentPrice: priceId,
          amount: Number(data?.shipmentPriceAmounts?.[priceId] || 0),
        })),
      // send only the relevant party, null the other
      vendor: isVendorShipment ? data?.vendor?._id : null,
      customer: isCustomerShipment ? data?.customer?._id : null,
      amount: submittedAmount,
      refundType: isCod ? null : data?.refundType?._id,
      refundMethod: null,
      bankName: null,
      bankAccount: null,
      isTransferToCOD: isCod,
      codAmount: codAmt,
      originalChargeAmount: originalCharge,
      adjustedChargeAmount: remainingCharge,
      adjustedPrice: data.adjustedPrice || (data.shipmentPrice?.[0] ?? null),
      adjustmentMessage:
        data.adjustmentMessage ||
        (isCod
          ? `Transfer Rs. ${codAmt} from mistaken Charge into COD.`
          : ""),
    });
  };

  // Reset form on edit load
  useEffect(() => {
    if (isLoadingRefundTypes || isFetchingRefundTypes) return;

    if (!shipmentRefundDetail) {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity || null,
        account: viewEntity?.entity?.account || null,
      });
      return;
    }

    const refundType = refundTypeList.find(
      (type) =>
        type._id ===
        (shipmentRefundDetail.refundType?._id || shipmentRefundDetail.refundType)
    );

    reset({
      ...INITIAL_STATE,
      ...shipmentRefundDetail,
      account: shipmentRefundDetail.account || null,
      entity: shipmentRefundDetail.entity || null,
      shipment: shipmentRefundDetail.shipment || null,
      shipmentPrice: shipmentRefundDetail.shipmentPrice || [],
      shipmentPriceAmounts: Object.fromEntries(
        (shipmentRefundDetail.shipmentPriceAdjustments || []).map((item) => [
          item.shipmentPrice?._id || item.shipmentPrice,
          item.amount,
        ])
      ),
      vendor: shipmentRefundDetail.vendor || null,
      customer: shipmentRefundDetail.customer || null,
      refundType: refundType
        ? { _id: refundType._id, label: refundType.name }
        : null,
      refundDate: shipmentRefundDetail.refundDate
        ? new Date(shipmentRefundDetail.refundDate).toISOString().slice(0, 10)
        : getDateAuto(),

      isTransferToCOD: shipmentRefundDetail.isTransferToCOD || shipmentRefundDetail.refundMethod === "COD Transfer",
      codAmount: shipmentRefundDetail.codAmount || "",
      originalChargeAmount: shipmentRefundDetail.originalChargeAmount || 0,
      adjustedChargeAmount: shipmentRefundDetail.adjustedChargeAmount || 0,
      adjustedPrice: shipmentRefundDetail.adjustedPrice || null,
      adjustmentMessage: shipmentRefundDetail.adjustmentMessage || "",
    });
  }, [
    shipmentRefundDetail,
    reset,
    viewEntity,
    refundTypeList,
    isLoadingRefundTypes,
    isFetchingRefundTypes,
  ]);

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  useEffect(() => {
    setShipmentSearchInput("");
  }, [_watchAccount?._id, _watchEntity?._id]);

  const debouncedShipmentSearch = useMemo(
    () => debounce((term) => {
      if (_watchAccount?._id && _watchEntity?._id) {
        fetchShipments({
          account: _watchAccount._id,
          entity: _watchEntity._id,
          query: term || "",
          limit: 5,
          includeUnpaid: isCodTransfer,
          ...(shipmentRefundDetail?.shipment?._id && {
            excludeShipmentId: shipmentRefundDetail.shipment._id,
          }),
        });
      }
    }, 400),
    [
      _watchAccount?._id,
      _watchEntity?._id,
      fetchShipments,
      shipmentRefundDetail?.shipment?._id,
      isCodTransfer,
    ]
  );

  useEffect(() => {
    debouncedShipmentSearch(shipmentSearchInput);
    return () => debouncedShipmentSearch.cancel();
  }, [shipmentSearchInput, debouncedShipmentSearch]);

  // Fetch prices when shipment changes
  useEffect(() => {
    if (_watchShipment?._id) {
      fetchShipmentPrices({
        shipmentId: _watchShipment._id,
        includeUnpaid: isCodTransfer,
      });
      if (shipmentRefundDetail?.shipment?._id !== _watchShipment._id) {
        setValue("shipmentPrice", []);
        setValue("amount", "");
      }
    }
  }, [_watchShipment?._id, fetchShipmentPrices, setValue, shipmentRefundDetail?.shipment?._id, isCodTransfer]);

  // Fetch prices for edit mode
  useEffect(() => {
    if (shipmentRefundDetail?.shipment?._id) {
      fetchShipmentPrices({
        shipmentId: shipmentRefundDetail.shipment._id,
        includeUnpaid: isCodTransfer,
      });
    }
  }, [shipmentRefundDetail, fetchShipmentPrices, isCodTransfer]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Account */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("shipment", null);
                      setValue("shipmentPrice", []);
                      setValue("vendor", null);
                      setValue("customer", null);
                      setValue("amount", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {/* Entity */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipment", null);
                      setValue("shipmentPrice", []);
                      setValue("vendor", null);
                      setValue("customer", null);
                      setValue("amount", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          {/* Transfer to COD Toggle */}
          {!hideChargesAndCod && (
            <Grid item xs={12}>
              <Controller
                name="isTransferToCOD"
                control={control}
                render={({ field }) => (
                  <AppSwitch
                    {...field}
                    checked={Boolean(field.value)}
                    label="Transfer to COD (Adjust mistakenly added Charge)?"
                    onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked);
                      if (checked) {
                        setValue(
                          "shipmentPrice",
                          (priceData?.prices || [])
                            .filter((price) => price.chargeType === "Charge")
                            .filter((price) => _watchShipmentPrice.includes(price._id))
                            .map((price) => price._id),
                        );
                        setValue("refundType", null);
                        if (_watchCodAmount) {
                          setValue("amount", _watchCodAmount);
                        }
                      } else {
                        setValue("amount", priceData?.totalCharge ?? "");
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {/* Refund Type */}
          {!isCodTransfer && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="refundType"
                control={control}
                rules={{ required: "Refund Type is required." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Refund Type"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      if (REFUND_TYPES_WITHOUT_CHARGES_OR_COD.includes(data?.label)) {
                        setValue("isTransferToCOD", false);
                      }
                    }}
                    getOptionLabel={(option) => option?.label ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={_mappedRefundTypes}
                    loading={isLoadingRefundTypes || isFetchingRefundTypes}
                    loadingText="Loading Refund Types..."
                    disabled={isLoadingRefundTypes || isFetchingRefundTypes}
                    helperText={errors?.refundType?.message}
                    error={!!errors?.refundType}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Refund Type"
                        required
                        error={!!errors?.refundType}
                        helperText={errors?.refundType?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {(isLoadingRefundTypes || isFetchingRefundTypes) && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {/* Shipment */}
          <Grid item md={6}>
            <Controller
              name="shipment"
              control={control}
              rules={{ required: "Please select a shipment." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Shipment"
                  value={value ?? null}
                  onChange={(_, data) => {
                    debouncedShipmentSearch.cancel();
                    onChange(data);
                    setValue("vendor", data?.vendor || null);
                    setValue("customer", data?.customerId || null);
                  }}
                  onInputChange={(_, newInput, reason) => {
                    if (reason !== "reset") setShipmentSearchInput(newInput);
                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipmentOptions}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipment?.message}
                  error={!!errors?.shipment}
                />
              )}
            />
          </Grid>

          {/* Vendor — only when shipment belongs to a vendor */}
          {isVendorShipment && (
            <Grid item md={6}>
              <Controller
                name="vendor"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Vendor"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option?.shopName || option?.ownerName || ""
                    }
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={vendorOptions}
                    disabled={!_watchShipment}
                    helperText={
                      vendorOptions.length === 0
                        ? "No vendor assigned to this shipment"
                        : ""
                    }
                    error={!!errors?.vendor}
                  />
                )}
              />
            </Grid>
          )}

          {/* Customer — only when shipment belongs to a customer */}
          {isCustomerShipment && (
            <Grid item md={6}>
              <Controller
                name="customer"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Customer"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option?.name
                        ? `${option.name}${option?.phone ? ` — ${option.phone}` : ""}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={customerOptions}
                    disabled={!_watchShipment}
                    helperText={
                      customerOptions.length === 0
                        ? "No customer assigned to this shipment"
                        : ""
                    }
                    error={!!errors?.customer}
                  />
                )}
              />
            </Grid>
          )}

          {/* Price Lines — read only, auto-filled from shipment */}
          {_watchShipment && !hideChargesAndCod && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                {loadingShipmentPrices ? (
                  <CircularProgress size={20} />
                ) : priceData?.prices?.length ? (
                  priceData.prices.map((p) => (
                    <Box key={p._id} display="flex" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={_watchShipmentPrice.includes(p._id)}
                      disabled={isCodTransfer && p.chargeType === "Cash on Delivery"}
                      onChange={(event) => {
                          const next = event.target.checked
                            ? [..._watchShipmentPrice, p._id]
                            : _watchShipmentPrice.filter((id) => id !== p._id);
                          setValue("shipmentPrice", next, { shouldValidate: true });
                          if (event.target.checked) {
                            setValue(`shipmentPriceAmounts.${p._id}`, Number(p.charge || 0));
                          }
                        }}
                      />
                      <Typography variant="body2">
                        {p.feeItem?.name ?? p.chargeType} ({p.chargeType}) — Rs. {p.charge}
                        {p.paymentStatus ? ` — ${p.paymentStatus}` : ""}
                      </Typography>
                      {_watchShipmentPrice.includes(p._id) && (
                        <TextField
                          size="small"
                          type="number"
                          label={isCodTransfer ? "Transfer amount" : "Refund amount"}
                          value={shipmentPriceAmounts[p._id] ?? p.charge}
                          error={Number(shipmentPriceAmounts[p._id]) > Number(p.charge)}
                          helperText={
                            Number(shipmentPriceAmounts[p._id]) > Number(p.charge)
                              ? `Cannot exceed Rs. ${p.charge}`
                              : `Maximum Rs. ${p.charge}`
                          }
                          onChange={(event) => {
                            const rawValue = event.target.value;
                            const nextValue = rawValue === ""
                              ? ""
                              : Math.min(Number(rawValue), Number(p.charge));
                            setValue(`shipmentPriceAmounts.${p._id}`, nextValue, {
                              shouldValidate: true,
                            });
                          }}
                          inputProps={{ min: 0.01, max: p.charge, step: "0.01" }}
                          sx={{ ml: 1, width: 150 }}
                        />
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No collected, unrefunded price lines are available for this shipment.
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* If COD Transfer: show COD Transfer panel; hide refundType, amount, refundMethod, bank */}
          {isCodTransfer ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  border: "1px solid #90caf9",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  COD Transfer & Price Adjustment
                </Typography>
                <Typography variant="caption" paragraph>
                  Convert mistakenly added Charge into Cash on Delivery price. Upon Finance approval, a new COD price will be created for vendor remittance and the Charge price will be reduced.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Original Charge Amount"
                      size="small"
                      disabled
                      value={_watchOriginalCharge || priceData?.totalCharge || 0}
                      InputProps={{ startAdornment: "Rs. " }}
                      helperText="Total charge currently recorded"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label="COD Amount to Transfer"
                      size="small"
                      value={selectedCodAmount}
                      disabled
                      helperText="Sum of the selected full or partial Charge amounts"
                      InputProps={{ startAdornment: "Rs. " }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="adjustmentMessage"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Adjustment Note / Audit Message"
                          size="small"
                          multiline
                          rows={2}
                          placeholder="e.g. Mistakenly added COD in Charge; transferring Rs. 18,000 into COD price."
                          helperText="Clear explanation of the COD transfer for finance approval and audit history"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : (
            <>
              {/* Amount */}
              <Grid item md={6}>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: "Amount is required.",
                    min: { value: 0, message: "Amount must be positive." },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      type="number"
                      label="Refund Amount"
                      size="small"
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
              </Grid>

            </>
          )}

          {/* Refund Status */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="refundStatus"
              control={control}
              rules={{ required: "Refund Status is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  select
                  label="Refund Status"
                  size="small"
                  error={!!errors.refundStatus}
                  helperText={errors.refundStatus?.message}
                  disabled
                >
                  {REFUND_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          {/* Refund Date */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="refundDate"
              control={control}
              rules={{ required: "Refund Date is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="date"
                  label="Refund Date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={field.value || getDateAuto()}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors.refundDate}
                  helperText={errors.refundDate?.message}
                />
              )}
            />
          </Grid>

          {/* Is Active (edit mode only) */}
          {shipmentRefundDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}

          {/* Refund Reason */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="refundReason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Refund Reason"
                  size="small"
                  multiline
                  rows={2}
                  error={!!errors.refundReason}
                  helperText={errors.refundReason?.message}
                />
              )}
            />
          </Grid>

        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy || isLoadingRefundTypes || isFetchingRefundTypes}
        >
          {shipmentRefundDetail ? "Update Refund" : "Add Refund"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentRefundForm;
